import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Camera, Heart, Baby, Flower2, Church, BookOpen, MapPin, Gift } from "lucide-react";
import { HERO_TEXT, HERO_IMAGE, TARIFS, GALLERY_CATEGORIES, SITE_INFO, LOGO_URL } from "../mock";

const iconMap = { Camera, Heart, Baby, Flower2, Church, BookOpen };

export default function Home() {
  const featuredImages = GALLERY_CATEGORIES.slice(0, 6);

  return (
    <div className="bg-black text-white">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <div
            className="absolute inset-0"
            style={{
              background: "radial-gradient(ellipse at center, #1a1a1a 0%, #0a0a0a 50%, #000 100%)"
            }}
          />
          {/* subtle texture/grain */}
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: "radial-gradient(circle at 20% 30%, rgba(201,169,97,0.08) 0%, transparent 45%), radial-gradient(circle at 80% 70%, rgba(201,169,97,0.06) 0%, transparent 50%)"
            }}
          />
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl fade-in py-24">
          <div className="relative inline-block fade-in-delay-1">
            <div
              className="absolute inset-0 -m-12"
              style={{
                background: "radial-gradient(ellipse, rgba(245,230,200,0.55) 0%, rgba(245,230,200,0.3) 30%, rgba(201,169,97,0.15) 60%, transparent 85%)",
                filter: "blur(30px)"
              }}
            />
            <img
              src={LOGO_URL}
              alt="Ludovic Garcia Photographe"
              className="relative h-64 md:h-80 lg:h-96 mx-auto mb-6"
            />
          </div>
          <p className="text-gold text-sm tracking-[0.3em] uppercase mt-4 mb-6 fade-in-delay-2">
            Photographe Professionnel
          </p>
          <div className="gold-divider my-6 fade-in-delay-2" />
          <p className="text-xl md:text-2xl text-white/90 italic fade-in-delay-2 font-light"
             style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {HERO_TEXT.tagline}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10 fade-in-delay-3">
            <Link to="/galerie" className="btn-gold">
              Découvrir la galerie
              <ArrowRight size={16} />
            </Link>
            <Link to="/reservation" className="btn-gold-filled">
              Réserver une séance
            </Link>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10">
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-gold to-transparent animate-pulse" />
        </div>
      </section>

      {/* INTRO - comme le site officiel avec texte + photo */}
      <section className="py-24 px-6 bg-black">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-gold text-sm tracking-[0.3em] uppercase mb-4">Bienvenue</p>
            <h2
              className="text-4xl md:text-5xl mb-6 section-title italic"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
            >
              {HERO_TEXT.intro}
            </h2>
            <div className="gold-divider" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            {/* Texte à gauche */}
            <div className="space-y-5 text-gray-200 leading-relaxed order-2 md:order-1">
              <p className="text-base md:text-lg">
                Je suis de <span className="text-gold">Fos Sur Mer</span> et je peux me déplacer dans toute la France si défraiement pris en compte.
              </p>
              <p className="text-base md:text-lg">
                Création de books en ligne / papier et shootings sur demande.
              </p>
              <div className="pt-2 space-y-2">
                <p className="text-base md:text-lg italic">
                  <span className="text-gold">PHOTOS</span>
                </p>
                <p className="text-base md:text-lg italic">
                  <span className="text-gold">Événementiels :</span> Mariage, Baptême, Anniversaire, Grossesse, Naissance, etc.
                </p>
              </div>
              <p className="text-base md:text-lg pt-2">
                Je suis ouvert à vos divers projets.
              </p>
              <p className="text-gray-400 text-sm md:text-base italic pt-2">
                Au plaisir de vous lire, je reste à votre entière disposition pour tout renseignements complémentaires.
              </p>
              <p className="text-gold text-sm md:text-base pt-3 tracking-wider uppercase">
                Mes tarifs sont affichés dans la galerie Tarifs
              </p>
              <p className="text-gray-300 text-sm pt-2">
                Me contactez sur Instagram / Facebook / e-mail.
              </p>

              {/* Réseaux sociaux */}
              <div className="pt-4 border-t border-gold/20 mt-6 space-y-1.5 text-sm text-gray-300">
                <p><span className="text-gold">Facebook :</span> Ludovic Garcia</p>
                <p><span className="text-gold">Page FaceBook :</span> Ludovic garcia photography</p>
                <p><span className="text-gold">Instagram :</span> Ludovic_garcia_photography</p>
                <p><span className="text-gold">Tiktok photographe :</span> lgphotography13</p>
                <p><span className="text-gold">Snap :</span> lgphotography13</p>
              </div>

              <p className="text-gray-400 italic pt-5"
                 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.25rem' }}>
                Cordialement, LG.
              </p>
            </div>

            {/* Photo à droite */}
            <div className="order-1 md:order-2">
              <div className="relative">
                <div className="absolute -inset-2 border border-gold/30" />
                <div className="aspect-[3/4] overflow-hidden relative">
                  <img
                    src="/ludovic.jpg"
                    alt="Ludovic Garcia Photographe"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRESTATIONS */}
      <section className="py-24 px-6 bg-gradient-to-b from-black via-zinc-950 to-black">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-gold text-sm tracking-[0.3em] uppercase mb-4">Mes Prestations</p>
            <h2 className="text-4xl md:text-5xl mb-6 section-title"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Un service sur-mesure
            </h2>
            <div className="gold-divider" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TARIFS.map((tarif) => {
              const Icon = iconMap[tarif.icon] || Camera;
              return (
                <Link
                  to="/tarifs"
                  key={tarif.id}
                  className="group border border-white/10 hover:border-gold p-8 transition-colors duration-500 bg-zinc-950/50"
                >
                  <Icon size={32} className="text-gold mb-4" strokeWidth={1} />
                  <h3 className="text-2xl mb-2"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                    {tarif.title}
                  </h3>
                  <p className="text-gold text-3xl mb-4 italic"
                     style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {tarif.priceDisplay}
                  </p>
                  <p className="text-gray-400 text-sm leading-relaxed line-clamp-3">
                    {tarif.description[0]}
                  </p>
                  <div className="mt-6 flex items-center gap-2 text-gold text-xs tracking-wider uppercase">
                    En savoir plus <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-gold text-sm tracking-[0.3em] uppercase mb-4">Portfolio</p>
            <h2 className="text-4xl md:text-5xl mb-6 section-title"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Galerie
            </h2>
            <div className="gold-divider" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {featuredImages.map((img) => (
              <Link to="/galerie" key={img.id} className="gallery-item aspect-[3/4] block relative">
                <img
                  src={img.cover}
                  alt={img.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/40 hover:bg-black/20 transition-colors flex items-end p-4">
                  <p className="text-white italic text-xl"
                     style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, textShadow: "0 2px 8px rgba(0,0,0,0.9)" }}>
                    {img.name}
                  </p>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/galerie" className="btn-gold">
              Voir toute la galerie
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA GIFT CARDS */}
      <section className="py-24 px-6 bg-gradient-to-br from-zinc-950 to-black">
        <div className="max-w-4xl mx-auto text-center">
          <Gift size={48} className="text-gold mx-auto mb-6" strokeWidth={1} />
          <h2 className="text-4xl md:text-5xl mb-6 section-title"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Offrez une séance photo
          </h2>
          <div className="gold-divider mb-8" />
          <p className="text-gray-300 text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
            Faites plaisir à vos proches avec une carte cadeau Ludovic Garcia Photography.
            Un cadeau unique, une expérience inoubliable. <span className="text-gold">Sans date limite d'utilisation.</span>
          </p>
          <Link to="/cartes-cadeaux" className="btn-gold-filled">
            Acheter une carte cadeau
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* LOCATION */}
      <section className="py-16 px-6 border-t border-gold/20">
        <div className="max-w-4xl mx-auto text-center">
          <MapPin size={32} className="text-gold mx-auto mb-4" strokeWidth={1} />
          <p className="text-gold text-sm tracking-[0.3em] uppercase mb-3">Localisation</p>
          <p className="text-2xl text-white mb-2"
             style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Basé à {SITE_INFO.location}
          </p>
          <p className="text-gray-400">Déplacements dans toute la France</p>
          <p className="text-gray-500 text-xs mt-3 italic">
            ⚠️ Les frais de route (carburant, péage) sont à la charge du client
          </p>
        </div>
      </section>
    </div>
  );
}
